import { useState, useEffect } from 'react';
import { useToast } from '@/hooks/use-toast';
import { RoleWithPermissions, FeaturePermission } from '@/types/userRole';
import { PERMISSIONS } from '@/utils/roleManagement';

export const useMasterRoles = () => {
  const { toast } = useToast();
  const [roles, setRoles] = useState<RoleWithPermissions[]>([
    {
      id: '0',
      name: 'Master Admin',
      description: 'Highest possible access, including all system, billing, and license controls',
      permissions: Object.values(PERMISSIONS),
      userCount: 1,
      isDefault: false,
      isSystem: true
    },
    {
      id: '4',
      name: 'Reseller',
      description: 'Can manage their own clients and customize branding',
      permissions: [
        PERMISSIONS.VIEW_CAMPAIGNS,
        PERMISSIONS.CREATE_CAMPAIGNS,
        PERMISSIONS.VIEW_USERS,
        PERMISSIONS.VIEW_ANALYTICS,
        PERMISSIONS.MANAGE_WHITE_LABEL,
        PERMISSIONS.CUSTOMIZE_DESIGN
      ],
      userCount: 1,
      isDefault: false,
      isSystem: true
    },
    {
      id: '1',
      name: 'Administrator',
      description: 'Full access to all features and settings',
      permissions: Object.values(PERMISSIONS),
      userCount: 2,
      isDefault: false,
      isSystem: true
    },
    {
      id: '3',
      name: 'Manager',
      description: 'Can manage campaigns and invite users',
      permissions: [
        PERMISSIONS.VIEW_CAMPAIGNS,
        PERMISSIONS.CREATE_CAMPAIGNS,
        PERMISSIONS.EDIT_CAMPAIGNS,
        PERMISSIONS.VIEW_USERS,
        PERMISSIONS.INVITE_USERS,
        PERMISSIONS.VIEW_ANALYTICS
      ],
      userCount: 3,
      isDefault: false,
      isSystem: true
    },
    {
      id: '2',
      name: 'User',
      description: 'Basic access to use the system',
      permissions: [
        PERMISSIONS.VIEW_CAMPAIGNS,
        PERMISSIONS.VIEW_ANALYTICS
      ],
      userCount: 5,
      isDefault: true,
      isSystem: true
    }
  ]);

  const [isLoading, setIsLoading] = useState(false);
  const [availablePermissions, setAvailablePermissions] = useState<FeaturePermission[]>([]);

  useEffect(() => {
    const permissionsArray: FeaturePermission[] = Object.entries(PERMISSIONS).map(([key, value]) => {
      const parts = value.split(':');
      const action = parts[0];
      const resource = parts[1] || 'general';
      return {
        id: value,
        name: key.split('_').map(word => word.charAt(0) + word.slice(1).toLowerCase()).join(' '),
        description: `Allows ${action} operations on ${resource}`,
        category: resource,
        isEnabled: true
      };
    });
    const groupedPermissions: Record<string, FeaturePermission[]> = {};
    permissionsArray.forEach(permission => {
      if (!groupedPermissions[permission.category]) {
        groupedPermissions[permission.category] = [];
      }
      groupedPermissions[permission.category].push(permission);
    });
    const sortedPermissions: FeaturePermission[] = [];
    Object.keys(groupedPermissions).sort().forEach(category => {
      sortedPermissions.push(...groupedPermissions[category]);
    });
    setAvailablePermissions(sortedPermissions);
  }, []);

  const addRole = async (roleData: Omit<RoleWithPermissions, 'id' | 'userCount'>) => {
    if (roleData.name.toLowerCase().includes('master admin')) {
      toast({
        title: "Not allowed",
        description: "Master Admin role already exists and cannot be created.",
        variant: "destructive",
      });
      return;
    }
    setIsLoading(true);
    try {
      await new Promise(resolve => setTimeout(resolve, 800));
      const newRole: RoleWithPermissions = {
        ...roleData,
        id: `role-${Date.now()}`,
        userCount: 0
      };
      setRoles(prevRoles => [...prevRoles, newRole]);
      toast({
        title: "Role Created",
        description: `Successfully created role: ${roleData.name}`,
      });
      return newRole;
    } catch (error) {
      toast({
        title: "Error",
        description: "Failed to create role",
        variant: "destructive",
      });
      throw error;
    } finally {
      setIsLoading(false);
    }
  };

  const updateRole = async (roleId: string, roleData: Partial<RoleWithPermissions>) => {
    setIsLoading(true);
    try {
      await new Promise(resolve => setTimeout(resolve, 800));
      setRoles(prevRoles => 
        prevRoles.map(role => 
          role.id === roleId ? { ...role, ...roleData } : role
        )
      );
      toast({
        title: "Role Updated",
        description: `Successfully updated role permissions`,
      });
    } catch (error) {
      toast({
        title: "Error",
        description: "Failed to update role",
        variant: "destructive",
      });
      throw error;
    } finally {
      setIsLoading(false);
    }
  };

  const deleteRole = async (roleId: string) => {
    setIsLoading(true);
    try {
      const role = roles.find(r => r.id === roleId);
      if (role?.userCount && role.userCount > 0) {
        throw new Error("Cannot delete a role with assigned users");
      }
      if (role?.isSystem) {
        throw new Error("Cannot delete a system role");
      }
      await new Promise(resolve => setTimeout(resolve, 800));
      setRoles(prevRoles => prevRoles.filter(role => role.id !== roleId));
      toast({
        title: "Role Deleted",
        description: `Successfully deleted role`,
      });
    } catch (error) {
      toast({
        title: "Error",
        description: error instanceof Error ? error.message : "Failed to delete role",
        variant: "destructive",
      });
      throw error;
    } finally {
      setIsLoading(false);
    }
  };

  return {
    roles,
    isLoading,
    availablePermissions,
    addRole,
    updateRole,
    deleteRole,
  };
};
